import React, { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import AuthLayout from "layouts/Auth";
import MainLayout from "layouts/Main";
import DetailLayout from "layouts/Detail";
import StatisticsLayout from "layouts/Statistics";
import ProtectedRoute from "components/ProtectedRoute";
import Dashboard from "layouts/Dashboard";
import { DashboardSection } from "layouts/Dashboard/constants/types";
import Result from "layouts/Result";

// Lazy components
const Login = lazy(() => import("views/Auth/Login"));

const Devices = lazy(() => import("views/Main/Devices"));
const DashboardDevices = lazy(() => import("views/Dashboard/Devices"));
const DashboardAssemblies = lazy(() => import("views/Dashboard/Assemblies"));
const DashboardTestFixtures = lazy(() => import("views/Dashboard/TestFixtures"));
const DashboardFirmwareUpdates = lazy(() => import("views/Dashboard/FirmwareUpdates"));

const Assemblies = lazy(() => import("views/Main/Assemblies"));
const Tests = lazy(() => import("views/Detail/Tests"));

//Statistics layout related lazy components...
const DeviceStatistics = lazy(() => import("views/Statistics/Devices"));
const AssemblyStatistics = lazy(() => import("views/Statistics/Assemblies"));
const AssemblyResult = lazy(() => import("views/Result/Assembly"));
const CalibrationResult = lazy(() => import("views/Result/Calibration"));

// A simple fallback for Suspense while components are loading

const routes = createBrowserRouter([
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "*",
        element: <Navigate to="/auth/login" replace />,
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: "/dashboard",
        element: <Dashboard />,
        children: [
          {
            index: true,
            element: <Navigate to={DashboardSection.DEVICES} replace />,
          },
          {
            path: DashboardSection.DEVICES,
            element: <DashboardDevices />,
          },
          {
            path: DashboardSection.ASSEMBLIES,
            element: <DashboardAssemblies />,
          },
          {
            path: DashboardSection.TEST_FIXTURES,
            element: <DashboardTestFixtures />,
          },
          {
            path: DashboardSection.FIRMWARE_UPDATES,
            element: <DashboardFirmwareUpdates />,
          },
          {
            path: DashboardSection.ANALYTICS,
            element: <DeviceStatistics />,
          },
          {
            path: "*",
            element: <Navigate to={DashboardSection.DEVICES} replace />,
          },
        ],
      },
      {
        path: "/result",
        element: <Result />,
        children: [
          {
            path: "assembly",
            element: <AssemblyResult />,
          },
          {
            path: "calibration",
            element: <CalibrationResult />,
          },
        ],
      },
      {
        path: "/main",
        element: <MainLayout />,
        children: [
          {
            path: "devices/:tab",
            element: <Devices />,
          },
          {
            path: "assemblies/:tab",
            element: <Assemblies />,
          },
          {
            path: "*",
            element: <Navigate to="/main/devices/Information" />,
          },
        ],
      },
      {
        path: "/statistics",
        element: <StatisticsLayout />,
        children: [
          {
            path: "devices/:tab",
            element: <DeviceStatistics />,
          },
          {
            path: "assemblies/:tab",
            element: <AssemblyStatistics />,
          },
        ],
      },
      {
        path: "/detail",
        element: <DetailLayout />,
        children: [
          {
            path: "tests/:type",
            element: <Tests />,
          },
          {
            path: "*",
            element: <Navigate to="/main/devices/Information" replace />,
          },
        ],
      },
      {
        path: "*",
        element: <Navigate to="/dashboard/devices" replace />,
      },
    ],
  },
]);

export default routes;
